import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import { useBpV2State } from "../../../context/BusinessPlanV2Context";
import { GlobalState } from "../../../context/GlobalContext";
import { AuthState } from "context/AuthContext";

import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { grey, red } from "@mui/material/colors";

import { API, toInLakhs, canApproveByHierarchy, canFinalApproveBp, displayNameFromEmail } from "../data";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";

const modalStyle = {
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: 720,
	maxHeight: "82vh",
	bgcolor: "background.paper",
	borderRadius: 3,
	boxShadow: "0 20px 60px rgba(15, 23, 42, 0.25)",
	border: `1px solid ${grey[200]}`,
	p: 3.5,
	overflow: "auto",
};

const statusColors = {
	DRAFT: "default",
	SUBMITTED: "warning",
	APPROVED: "success",
	REJECTED: "error",
};

const StoreBpModal = ({ open, onClose }) => {
	const { selectedStore, setSelectedSubmission } = useBpV2State();
	const { setLoadingOpen, setSnackBar, snackBar, setSnackBarOpen } = GlobalState();
	const { user } = AuthState();
	const navigate = useNavigate();

	const [storeData, setStoreData] = useState(null);
	const [loadingLocal, setLoadingLocal] = useState(false);
	const [rejectDialog, setRejectDialog] = useState({ open: false, submissionId: null, remark: "" });

	const showSnack = (message, severity = "error") => {
		setSnackBarOpen(true);
		setSnackBar({ ...snackBar, message, severity });
	};

	const fetchStoreData = async () => {
		if (!selectedStore?.id) return;
		try {
			setLoadingLocal(true);
			const { data } = await axios.get(`${API.GET_STORE}?id=${selectedStore.id}`);
			if (data.status === "success") {
				setStoreData(data.result);
			}
		} catch (err) {
			showSnack(parseErrMsgFromError(err));
		} finally {
			setLoadingLocal(false);
		}
	};

	useEffect(() => {
		if (open && selectedStore?.id) fetchStoreData();
	}, [open, selectedStore?.id]);

	const handleCreateNew = async () => {
		try {
			setLoadingOpen(true);
			const { data } = await axios.post(API.CREATE_SUBMISSION, {
				bp_v2_store_id: selectedStore.id,
			});
			if (data.status === "success") {
				const submission = data.result.submission || data.result;
				setSelectedSubmission(submission);
				navigate(`/explore/business_plan_v2/store_bp?submission_id=${submission.id}`);
			}
		} catch (err) {
			console.log(err);
			showSnack(parseErrMsgFromError(err));
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleDuplicate = async (sourceId) => {
		try {
			setLoadingOpen(true);
			const { data } = await axios.post(API.DUPLICATE_SUBMISSION, {
				source_submission_id: sourceId,
				bp_v2_store_id: selectedStore.id,
			});
			if (data.status === "success") {
				showSnack("Duplicated", "success");
				fetchStoreData();
			}
		} catch (err) {
			console.log(err);
			showSnack(parseErrMsgFromError(err));
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleSelectSubmission = (sub) => {
		setSelectedSubmission(sub);
		navigate(`/explore/business_plan_v2/store_bp?submission_id=${sub.id}`);
	};

	const handleApproveToggle = async (e, submissionId) => {
		e?.stopPropagation?.();
		if (!submissionId) return;
		try {
			const sub = storeData?.submissions?.find((s) => s.id === submissionId);
			const isApproved = sub?.approvals?.some((s) => s.user_email === user?.email);
			if (isApproved) {
				await axios.delete(`${API.APPROVAL}/${submissionId}`);
			} else {
				await axios.post(`${API.APPROVAL}/${submissionId}`);
			}
			fetchStoreData();
		} catch (err) {
			showSnack(parseErrMsgFromError(err));
		}
	};

	const handleApprove = async (subId) => {
		try {
			setLoadingOpen(true);
			await axios.put(`${API.APPROVE}/${subId}`);
			showSnack("Approved", "success");
			fetchStoreData();
		} catch (err) {
			showSnack(parseErrMsgFromError(err));
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleUnapprove = async (subId) => {
		try {
			setLoadingOpen(true);
			await axios.put(`${API.UNAPPROVE}/${subId}`);
			showSnack("Un-approved", "success");
			fetchStoreData();
		} catch (err) {
			showSnack(parseErrMsgFromError(err));
		} finally {
			setLoadingOpen(false);
		}
	};

	const openRejectDialog = (e, submissionId) => {
		e?.stopPropagation?.();
		setRejectDialog({ open: true, submissionId, remark: "" });
	};

	const closeRejectDialog = () => setRejectDialog({ open: false, submissionId: null, remark: "" });

	const handleReject = async () => {
		if (!rejectDialog.remark.trim()) return;
		try {
			setLoadingOpen(true);
			await axios.put(`${API.REJECT}/${rejectDialog.submissionId}`, { remarks: rejectDialog.remark.trim() });
			showSnack("Submission rejected", "success");
			closeRejectDialog();
			fetchStoreData();
		} catch (err) {
			showSnack(parseErrMsgFromError(err));
		} finally {
			setLoadingOpen(false);
		}
	};

	const submissions = storeData?.submissions || [];
	const isFinalApprover = canFinalApproveBp(user?.role);

	return (
		<>
			<Modal open={open} onClose={onClose}>
				<Box sx={modalStyle}>
				<Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2.5}>
					<Stack>
						<Typography variant="h6" fontWeight={700}>
							{selectedStore?.store_name}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{selectedStore?.mapping_code} · {selectedStore?.brand} · Pick a submission or create one
						</Typography>
					</Stack>
					<IconButton onClick={onClose} size="small">
						<CloseIcon fontSize="small" />
					</IconButton>
				</Stack>

				<Divider sx={{ mb: 2 }} />

				{/* Submissions List */}
				{loadingLocal ? (
					<Box display="flex" justifyContent="center" py={4}>
						<CircularProgress size={28} />
					</Box>
				) : (
					<Stack spacing={1.25} mb={2.5}>
						{!submissions.length && (
							<Box
								sx={{
									textAlign: "center",
									py: 4,
									color: grey[500],
									border: `1px dashed ${grey[300]}`,
									borderRadius: 2,
								}}
							>
								<Typography variant="body2">No submissions yet for this store</Typography>
							</Box>
						)}
						{submissions.map((sub) => {
							const approvals = sub.approvals || [];
							const rejections = sub.rejections || [];
							const lastRejection = rejections.length
								? [...rejections].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
								: null;
							const isApprovedByMe = approvals.some((s) => s.user_email === user?.email);
							const canApprove =
								sub.status === "SUBMITTED" &&
								!sub.is_final &&
								canApproveByHierarchy(user?.role, sub.created_by_role);
							const showApproveBtn = canApprove || isApprovedByMe;
							return (
								<Box
									key={sub.id}
									sx={{
										p: 2,
										border: "1px solid",
										borderColor: sub.is_final ? "success.main" : sub.status === "REJECTED" ? "error.main" : grey[200],
										borderRadius: 2,
										bgcolor: sub.is_final ? "#e8f5e9" : sub.status === "REJECTED" ? "#fdecea" : "background.paper",
										cursor: "pointer",
										transition: "background-color 0.15s ease",
										"&:hover": { bgcolor: sub.is_final ? "#c8e6c9" : sub.status === "REJECTED" ? "#fadbd8" : grey[50] },
									}}
									onClick={() => handleSelectSubmission(sub)}
								>
									<Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
										<Stack spacing={0.75} flex={1} minWidth={0}>
											<Typography variant="subtitle2" fontWeight={600}>
												{sub.created_by_name} ({sub.created_by_role})
											</Typography>
											<Typography variant="caption" color="text.secondary">
												Qty: {sub.total_qty || 0} · NSV: {toInLakhs(sub.new_nsv)}L · DIS: {sub.new_dis || 0}%
												{sub.total_count > 0 && ` · Progress: ${sub.current_count}/${sub.total_count}`}
											</Typography>
											<Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
												<Typography variant="caption" fontWeight={600} color={grey[600]}>
													Approvals:
												</Typography>
												{approvals.map((s) => (
													<Chip
														key={s.id}
														label={`${displayNameFromEmail(s.user_name, s.user_email)} (${s.user_role || "—"})`}
														size="small"
														icon={<CheckCircleIcon />}
														color="success"
														variant="outlined"
													/>
												))}
												{!approvals.length && (
													<Typography variant="caption" color="text.secondary">
														None
													</Typography>
												)}
											</Stack>
											{lastRejection && sub.status === "REJECTED" && (
												<Stack
													direction="row"
													spacing={0.75}
													alignItems="flex-start"
													sx={{ bgcolor: "#fff", border: `1px solid ${red[200]}`, borderRadius: 1, p: 1, mt: 0.25 }}
												>
													<ErrorOutlineIcon fontSize="small" sx={{ color: red[600], mt: 0.15 }} />
													<Box>
														<Typography variant="caption" fontWeight={700} color={red[800]}>
															Rejected by {displayNameFromEmail(lastRejection.user_name, lastRejection.user_email)}
															{lastRejection.user_role ? ` (${lastRejection.user_role})` : ""}
														</Typography>
														<Typography variant="caption" color={grey[800]} display="block">
															{lastRejection.remark}
														</Typography>
													</Box>
												</Stack>
											)}
										</Stack>
										<Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
											<Chip label={sub.status} size="small" color={statusColors[sub.status]} />
											{sub.is_final && <Chip label="FINAL" size="small" color="success" />}
											{showApproveBtn && (
												<Button
													size="small"
													variant={isApprovedByMe ? "outlined" : "contained"}
													color={isApprovedByMe ? "error" : "success"}
													onClick={(e) => handleApproveToggle(e, sub.id)}
												>
													{isApprovedByMe ? "Remove Approval" : "Approve"}
												</Button>
											)}
											{canApprove && (
												<Button
													size="small"
													variant="outlined"
													color="error"
													onClick={(e) => openRejectDialog(e, sub.id)}
												>
													Reject
												</Button>
											)}
											<IconButton
												size="small"
												onClick={(e) => {
													e.stopPropagation();
													handleDuplicate(sub.id);
												}}
												title="Duplicate"
											>
												<ContentCopyIcon fontSize="small" />
											</IconButton>
											{isFinalApprover && sub.status === "SUBMITTED" && (
												<Button
													size="small"
													variant="contained"
													color="primary"
													onClick={(e) => {
														e.stopPropagation();
														handleApprove(sub.id);
													}}
												>
													Final Approve
												</Button>
											)}
											{isFinalApprover && sub.status === "APPROVED" && sub.is_final && (
												<Button
													size="small"
													variant="outlined"
													color="warning"
													onClick={(e) => {
														e.stopPropagation();
														handleUnapprove(sub.id);
													}}
												>
													Un-final
												</Button>
											)}
										</Stack>
									</Stack>
								</Box>
							);
						})}
					</Stack>
				)}

				<Button variant="contained" fullWidth startIcon={<AddRoundedIcon />} onClick={handleCreateNew} sx={{ borderRadius: 1.5 }}>
					Create New Submission
				</Button>
			</Box>
			</Modal>

			<Dialog open={rejectDialog.open} onClose={closeRejectDialog} maxWidth="sm" fullWidth>
				<DialogTitle>Reject submission</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="text.secondary" mb={2}>
						This sends the submission back to the creator to revise and resubmit. A remark is required.
					</Typography>
					<TextField
						autoFocus
						fullWidth
						multiline
						minRows={3}
						placeholder="Explain what needs to change…"
						value={rejectDialog.remark}
						onChange={(e) => setRejectDialog((prev) => ({ ...prev, remark: e.target.value }))}
					/>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2.5 }}>
					<Button onClick={closeRejectDialog} sx={{ textTransform: "none" }}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="error"
						disabled={!rejectDialog.remark.trim()}
						onClick={handleReject}
						sx={{ textTransform: "none" }}
					>
						Reject
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default StoreBpModal;
